/** Customer shipping address capture — matches backend checkout/shipping Zod (no methodId). */

export interface ShippingAddressDraft {
  recipientName: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  countryCode: string;
  phone: string;
  email: string;
}

/**
 * Checkout create payload for SHIPPING — address only; charge 0 or omit; no methodId.
 */
export interface ShippingCheckoutPayload {
  shippingChargeMinor?: 0;
  saveAddressToMembership?: boolean;
  address: {
    recipientName: string;
    line1: string;
    line2?: string | null;
    city: string;
    postalCode: string;
    countryCode: string;
    phone?: string | null;
    email?: string | null;
  };
}

export const EMPTY_SHIPPING_ADDRESS: ShippingAddressDraft = {
  recipientName: '',
  line1: '',
  line2: '',
  city: '',
  postalCode: '',
  countryCode: 'CZ',
  phone: '',
  email: '',
};

export function isShippingAddressComplete(draft: ShippingAddressDraft): boolean {
  return (
    draft.recipientName.trim().length > 0 &&
    draft.line1.trim().length > 0 &&
    draft.city.trim().length > 0 &&
    draft.postalCode.trim().length > 0 &&
    draft.countryCode.trim().length === 2
  );
}

export function toShippingCheckoutPayload(
  draft: ShippingAddressDraft,
  options?: {
    readonly shippingChargeMinor?: 0;
    readonly saveAddressToMembership?: boolean;
  },
): ShippingCheckoutPayload | null {
  if (!isShippingAddressComplete(draft)) {
    return null;
  }
  const phone = draft.phone.trim();
  const email = draft.email.trim();
  const line2 = draft.line2.trim();
  const includeCharge = options?.shippingChargeMinor === 0;
  return {
    ...(includeCharge ? { shippingChargeMinor: 0 as const } : {}),
    ...(options?.saveAddressToMembership === true
      ? { saveAddressToMembership: true }
      : {}),
    address: {
      recipientName: draft.recipientName.trim(),
      line1: draft.line1.trim(),
      line2: line2.length > 0 ? line2 : null,
      city: draft.city.trim(),
      postalCode: draft.postalCode.trim(),
      countryCode: draft.countryCode.trim().toUpperCase(),
      phone: phone.length > 0 ? phone : null,
      email: email.length > 0 ? email : null,
    },
  };
}
